// File: frontend/src/pages/PartnersLanding.js
//
// The public PesaFii Ambassadors programme page, served at /ambassador on the
// business host (https://pesafii.net/ambassador). App.js mounts it and
// redirects /ambassadors and /partners here.
//
// ── WHO THIS IS FOR ─────────────────────────────────────────────────────────
//
// Anyone deciding whether to become an Ambassador, readable without an
// account. The page NEVER redirects anyone away: a signed-in Ambassador sees
// "Open Ambassador Dashboard", but only as an action they choose.
//
// ── EVERY FIGURE COMES FROM THE ENGINE ──────────────────────────────────────
//
// The rate, the tier thresholds, the windows, the milestone amounts and
// whether sign-ups are open are fetched from GET /api/partners/program/public,
// which reads the same baselinePolicy() that prices commission. A hard-coded
// "10%" here would keep advertising the old rate through the first policy
// change, on the one page a stranger reads as an offer.
//
// ── IT RENDERS BEFORE THE FETCH RESOLVES ────────────────────────────────────
//
// No page spinner. The qualitative sections render immediately and the
// figures fill in. Sections that are nothing without their numbers (tiers,
// milestones) keep their headings and show a skeleton, then either the real
// figures or a plain "unavailable" note. Never a fallback figure.
//
// ── LAYOUT SHELL ────────────────────────────────────────────────────────────
//
// This page composes MarketingHeader + MarketingFooter itself instead of going
// through PublicLayout, because it needs a header the main site does not: the
// "Ambassadors" wordmark, lg-and-up anchor links and a state-aware CTA. The
// shell otherwise matches PublicLayout (share image, smooth anchors, colours,
// skip link, and the floating WhatsApp link with its Ambassador message).

import React, { useEffect, useState } from "react";
import { Box, useTheme, alpha } from "@mui/material";

import MarketingHeader from "../components/marketing/MarketingHeader";
import MarketingFooter from "../components/marketing/MarketingFooter";
import PartnersBackdrop from "../components/marketing/partners/PartnersBackdrop";
import { PartnersHero } from "../components/marketing/partners/PartnersHero";
import {
  HowItWorks, WhyJoin, EarningsExplained, ReferralKit, PartnersFAQ, PartnersFinalCTA,
} from "../components/marketing/partners/PartnersSections";
import { TierShowcase, MilestoneRewards } from "../components/marketing/partners/PartnersTiers";
import { CtaButton } from "../components/marketing/partners/PartnersPrimitives";
import { ScrollTrigger } from "../utils/gsapCore";
import usePageMeta from "../utils/usePageMeta";
import SkipToContent, { MAIN_CONTENT_ID } from "../components/marketing/SkipToContent";
import WhatsAppCta from "../components/marketing/WhatsAppCta";
import { WHATSAPP_MESSAGES } from "../config/publicSupport";
import { authService, partnerService } from "../services/api";
import { ambassadorUrl, mainUrl } from "../config/origins";

export const NAV_LINKS = [
  { label: "How it works", href: "#how-it-works" },
  { label: "Why join", href: "#why-join" },
  { label: "Tiers", href: "#tiers" },
  { label: "Rewards", href: "#rewards" },
  { label: "Rules", href: "#rules" },
];

const PAGE_URL = mainUrl("/ambassador");
const SHARE_IMAGE = "https://pesafii.net/pesafii-share.png";

/**
 * What the call-to-action areas offer this visitor.
 *
 *   signed out                 Become an Ambassador → Ambassador host /register
 *                              Sign in              → Ambassador host /login
 *   signed in, not Ambassador  Join Ambassador Program → openWorkspace(/join)
 *   signed in, Ambassador      Open Ambassador Dashboard → openWorkspace(/)
 *                              Back to My Business (only with a business)
 *   signed in, status unknown  a neutral action until /partners/me answers.
 *                              An error is "unknown", never "not an Ambassador":
 *                              offering Join to an existing Ambassador is wrong.
 *
 * Sign-ups closed (open_for_signups === false): no registration or join action
 * is offered, because that would be a dead end. The page says so plainly
 * (`closed`) and keeps whatever live actions remain. While the programme facts
 * are still loading, openness is unknown and the normal actions show.
 */
export function resolveCta({ user, partnerStatus, openForSignups }) {
  const closed = openForSignups === false;

  if (!user) {
    return {
      primary: closed ? null : { kind: "external", label: "Become an Ambassador", href: ambassadorUrl("/register") },
      secondary: { kind: "external", label: "Sign in", href: ambassadorUrl("/login") },
      closed,
    };
  }

  const business = user.tenant_id != null
    ? { kind: "route", label: "Back to My Business", to: "/dashboard" }
    : null;

  if (partnerStatus === "yes") {
    return {
      primary: { kind: "workspace", label: "Open Ambassador Dashboard", path: "/" },
      secondary: business,
      closed: false,
    };
  }

  if (partnerStatus === "no") {
    return closed
      ? { primary: null, secondary: business, closed: true }
      : { primary: { kind: "workspace", label: "Join Ambassador Program", path: "/join" }, secondary: null, closed: false };
  }

  return {
    primary: { kind: "workspace", label: "Go to Ambassador Program", path: "/" },
    secondary: null,
    closed: false,
  };
}

/** Accept only a well-formed programme response. */
function normaliseProgram(d) {
  if (!d || typeof d !== "object") return null;
  const rate = Number(d.commission_rate);
  const days = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : null);
  const tiers = (Array.isArray(d.tiers) ? d.tiers : [])
    .filter((t) => t && typeof t.key === "string" && typeof t.label === "string" && Number.isFinite(Number(t.threshold)))
    .map((t) => ({
      key: t.key,
      label: t.label,
      threshold: Number(t.threshold),
      reward_amount: t.reward_amount == null ? null : Number(t.reward_amount),
    }))
    .sort((a, b) => a.threshold - b.threshold);
  return {
    rate: Number.isFinite(rate) && rate > 0 && rate < 1 ? rate : null,
    tiers,
    activeWindowDays: days(d.active_window_days),
    graceWindowDays: days(d.grace_window_days),
    openForSignups: typeof d.open_for_signups === "boolean" ? d.open_for_signups : null,
  };
}

export default function PartnersLanding() {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";

  const [user] = useState(() => authService.getCurrentUser());
  const [program, setProgram] = useState(null);
  const [programStatus, setProgramStatus] = useState("loading");
  const [partnerStatus, setPartnerStatus] = useState(user ? "unknown" : "none");

  usePageMeta({
    title: "PesaFii Ambassadors | Refer networks. Earn as they grow.",
    description:
      "Refer hotspot businesses to PesaFii and earn a recurring share of PesaFii's qualifying revenue from every business you bring in. Five tiers, milestone rewards and withdrawals to mobile money.",
    ogImage: SHARE_IMAGE,
    canonical: PAGE_URL,
  });

  // ── The programme facts ───────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    partnerService.publicProgram()
      .then((d) => {
        if (cancelled) return;
        const p = normaliseProgram(d);
        if (!p) throw new Error("malformed programme response");
        setProgram(p);
        setProgramStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        // Degrade, never fabricate, and never silently: a public page that
        // loses its figures should be visible in the console.
        console.error("[ambassador/landing] programme facts unavailable", err);
        setProgramStatus("error");
      });
    return () => { cancelled = true; };
  }, []);

  // ── Is this visitor already an Ambassador? ────────────────────────────────
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    partnerService.me()
      .then((d) => {
        if (cancelled) return;
        setPartnerStatus(typeof d?.is_partner === "boolean" ? (d.is_partner ? "yes" : "no") : "unknown");
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[ambassador/landing] Ambassador status unknown", err);
        setPartnerStatus("unknown");
      });
    return () => { cancelled = true; };
  }, [user]);

  // ── Keep the scroll triggers honest ───────────────────────────────────────
  // The tier and milestone sections change height when the figures land, which
  // moves every trigger below them. The sections rebuild their own; this
  // re-measures everything else.
  useEffect(() => {
    if (programStatus === "loading") return undefined;
    const id = requestAnimationFrame(() => ScrollTrigger.refresh());
    return () => cancelAnimationFrame(id);
  }, [programStatus]);

  const tiers = program?.tiers || [];
  const ratePct = program?.rate != null ? Math.round(program.rate * 1000) / 10 : null;
  const tierSummary = tiers.length >= 2
    ? `${tiers.length} tiers, ${tiers[0].label} to ${tiers[tiers.length - 1].label}`
    : null;

  const cta = resolveCta({ user, partnerStatus, openForSignups: program ? program.openForSignups : null });

  // Header: compact. Signed out gets Sign in + the primary; signed in gets the
  // single most useful action.
  const headerActions = ({ stacked, close }) => {
    if (!user) {
      return (
        <>
          <CtaButton action={cta.secondary} variant="text" size="medium" fullWidth={stacked} onNavigate={close} sx={{ color: "text.primary" }} />
          {cta.primary && <CtaButton action={cta.primary} variant="contained" size="medium" fullWidth={stacked} onNavigate={close} />}
        </>
      );
    }
    const main = cta.primary || cta.secondary;
    return main
      ? <CtaButton action={main} variant={cta.primary ? "contained" : "outlined"} size="medium" fullWidth={stacked} onNavigate={close} />
      : null;
  };

  // Page-level surface tokens, all derived from theme.js.
  const vars = {
    "--amb-surface": alpha(theme.palette.background.paper, dark ? 0.9 : 0.92),
    "--amb-surface-strong": theme.palette.background.paper,
    "--amb-line": alpha(theme.palette.text.primary, dark ? 0.12 : 0.1),
    "--amb-accent-soft": alpha(theme.palette.primary.main, dark ? 0.14 : 0.1),
    "--amb-accent-line": alpha(theme.palette.primary.main, dark ? 0.42 : 0.36),
    "--amb-track": alpha(theme.palette.text.primary, dark ? 0.08 : 0.06),
  };

  return (
    <Box
      sx={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        bgcolor: "background.default", color: "text.primary",
        // clip, not hidden: hidden makes this a scroll container and would stop
        // the sticky header from sticking.
        overflowX: "clip",
        ...vars,
      }}
    >
      <style>{"@media (prefers-reduced-motion: no-preference){html{scroll-behavior:smooth}}"}</style>

      <SkipToContent />
      <MarketingHeader
        isAuthed={Boolean(user)}
        navLinks={NAV_LINKS}
        navBreakpoint="lg"
        brandSuffix="Ambassadors"
        renderActions={headerActions}
        mobileAction={cta.primary ? <CtaButton action={cta.primary} variant="contained" size="medium" /> : null}
      />

      <Box component="main" id={MAIN_CONTENT_ID} tabIndex={-1} sx={{ flex: 1, minWidth: 0, position: "relative", "&:focus": { outline: "none" } }}>
        <PartnersBackdrop />

        {/* The backdrop is fixed at z-index 0; everything readable sits above. */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <PartnersHero ratePct={ratePct} cta={cta} tierSummary={tierSummary} />
          <HowItWorks ratePct={ratePct} />
          <WhyJoin ratePct={ratePct} />
          <EarningsExplained rate={program?.rate ?? null} ratePct={ratePct} />
          <TierShowcase
            tiers={tiers}
            status={programStatus}
            activeWindowDays={program?.activeWindowDays}
            graceWindowDays={program?.graceWindowDays}
            ratePct={ratePct}
          />
          <MilestoneRewards tiers={tiers} status={programStatus} graceWindowDays={program?.graceWindowDays} />
          <ReferralKit />
          <PartnersFAQ
            ratePct={ratePct}
            activeWindowDays={program?.activeWindowDays}
            graceWindowDays={program?.graceWindowDays}
          />
          <PartnersFinalCTA cta={cta} ratePct={ratePct} />
        </Box>
      </Box>

      <MarketingFooter isAuthed={Boolean(user)} whatsappMessage={WHATSAPP_MESSAGES.ambassador} />
      <WhatsAppCta message={WHATSAPP_MESSAGES.ambassador} />
    </Box>
  );
}
